<!--
 * @Author: Tahiti
 * @Date: 2025-12-18
 * @Description: 特效模板选择弹框组件
 * @FilePath: /weryai-web/components/Effects/EffectsTemplateSelectModal.vue
-->
<template>
    <div
        v-if="visible"
        class="fixed z-[3000] inset-0 bg-black bg-opacity-70 flex items-center justify-center"
        @click.self="handleClose"
    >
        <div class="modal-container">
            <!-- 头部 -->
            <header class="modal-header">
                <div class="modal-title">
                    {{ type === 'VIDEO' ? t('effects.selectVideoEffect') : t('effects.selectImageEffect') }}
                </div>
                <div 
                    class="close-btn"
                    @click="handleClose"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6L18 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            </header>

            <!-- Tab 列表（仅视频模板显示） -->
            <div v-if="type === 'VIDEO' && tabs.length > 0" class="tabs-container">
                <div 
                    v-for="tab in tabs" 
                    :key="tab.id"
                    class="tab-item"
                    :class="{ active: activeTab === tab.id }"
                    @click="handleTabChange(tab.id)"
                >
                    {{ tab.name }}
                </div>
            </div>

            <!-- 模板列表 -->
            <div ref="scrollContainerRef" class="templates-grid" @scroll="handleScroll">
                <!-- Loading 状态 -->
                <div v-if="loading" class="loading-container">
                    <ClientOnly>
                        <l-dot-spinner size="40" speed="0.9" color="var(--el-menu-active-color)"></l-dot-spinner>
                    </ClientOnly>
                </div>
                
                <!-- 模板卡片 -->
                <div v-else class="templates-grid-inner">
                    <div 
                        v-for="item in displayItems" 
                        :key="item.id"
                        class="template-card"
                        @click="handleSelectTemplate(item)"
                    >
                        <!-- 封面图/视频 -->
                        <div
                            class="card-media"
                            @mouseenter="handleCardMediaEnter"
                            @mouseleave="handleCardMediaLeave"
                        >
                            <!-- 视频模板：先显示封面图片 -->
                            <img
                                v-if="type === 'VIDEO' && item.cover && !videoLoadedMap[item.id]"
                                :src="item.cover"
                                :alt="item.title"
                                class="media-img"
                            />
                            <!-- 视频模板：视频加载完成后显示 -->
                            <video
                                v-if="type === 'VIDEO' && item.video"
                                v-show="videoLoadedMap[item.id]"
                                :src="item.video"
                                class="media-img"
                                :poster="getResourceThumbnail(item.video) || item.cover"
                                loop
                                muted
                                playsinline
                                webkit-playsinline
                                preload="metadata"
                                @canplay="handleVideoLoaded(item.id)"
                            />
                            <!-- 视频模板：无视频时只显示封面 -->
                            <img
                                v-if="type === 'VIDEO' && item.cover && !item.video"
                                :src="item.cover"
                                :alt="item.title"
                                class="media-img"
                            />
                            <!-- 图片模板使用 img -->
                            <img 
                                v-else-if="type === 'IMAGE'"
                                :src="item.cover" 
                                :alt="item.title"
                                class="media-img"
                            />
                            <!-- 热度/点赞数 -->
                            <div 
                                v-if="item.likes" 
                                class="likes-badge"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M12.1108 4.03674C10.2908 4.58491 9.95763 6.13877 10.075 7.12569C8.77669 5.51397 8.83 3.65991 8.83 0.98877C4.66605 2.64689 5.63443 7.42763 5.5099 8.8794C4.46255 7.97408 4.26465 5.81096 4.26465 5.81096C3.15902 6.41179 2.60461 8.01638 2.60461 9.31794C2.60461 12.4654 5.02022 15.0168 7.99997 15.0168C10.9797 15.0168 13.3953 12.4654 13.3953 9.31794C13.3953 7.44747 12.0951 6.58464 12.1108 4.03674Z" fill="white"/>
                                </svg>
                                {{ formatNumber(item.likes) }}
                            </div>
                            <!-- 使用此模板按钮 -->
                            <button class="use-template-btn">
                                {{ t('effects.useThisTemplate') }}
                            </button>
                        </div>
                        <!-- 标题：内层 span 限制宽度并显示省略号（移动端需 min-width:0 才能生效） -->
                        <div class="card-title">
                            <span class="card-title__text">{{ item.title }}</span>
                        </div>
                    </div>
                    
                    <!-- 底部加载更多/没有更多 -->
                    <div v-if="type === 'IMAGE' && displayItems && displayItems.length > 0" class="load-more-container">
                        <ClientOnly>
                            <l-dot-spinner 
                                v-if="loadingMore" 
                                size="40" 
                                speed="0.9" 
                                color="var(--el-menu-active-color)"
                            ></l-dot-spinner>
                            <span v-else-if="!hasMore" class="no-more-text">
                                {{ t('inspiration.noMoreData') }}
                            </span>
                        </ClientOnly>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { getResourceThumbnail } from '~/utils/common'

interface TemplateItem {
    id: string | number
    slug?: string
    cover?: string
    title?: string
    likes?: number
    type?: string
    [key: string]: any
}

interface TabItem {
    id: string
    name: string
    tagId?: number
}

const props = withDefaults(defineProps<{
    visible: boolean
    type: 'VIDEO' | 'IMAGE'
    tabs?: TabItem[]
    items?: TemplateItem[]
    categoryItems?: Record<string, TemplateItem[]>
    loading?: boolean
    hasMore?: boolean
    loadingMore?: boolean
}>(), {
    visible: false,
    type: 'VIDEO',
    tabs: () => [],
    items: () => [],
    categoryItems: () => ({}),
    loading: false,
    hasMore: false,
    loadingMore: false
})

const emit = defineEmits<{
    'update:visible': [value: boolean]
    select: [item: TemplateItem]
    loadMore: []
}>()

const { t } = useI18n()

// 滚动容器引用
const scrollContainerRef = ref<HTMLElement | null>(null)

// 视频加载状态映射
const videoLoadedMap = ref<Record<string | number, boolean>>({})

// 视频加载完成
const handleVideoLoaded = (itemId: string | number) => {
    videoLoadedMap.value[itemId] = true
}

// 当前激活的 Tab
const activeTab = ref('')

// 初始化默认 Tab（每次打开或 tabs 数据变化时重置为第一个）
watch(
    [() => props.visible, () => props.tabs],
    ([visible, tabs]) => {
        if (visible && tabs && tabs.length > 0) {
            activeTab.value = tabs[0]?.id || ''
        }
        // 关闭弹框时重置视频加载状态
        if (!visible) {
            videoLoadedMap.value = {}
        }
    },
    { immediate: true }
)

// 显示的模板列表
const displayItems = computed(() => {
    if (props.type === 'IMAGE') {
        return props.items
    }
    // 视频模板按分类显示
    if (activeTab.value && props.categoryItems[activeTab.value]) {
        return props.categoryItems[activeTab.value]
    }
    return props.items
})

// 格式化数字
const formatNumber = (num: number): string => {
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
}

// Tab 切换
const handleTabChange = (tabId: string) => {
    activeTab.value = tabId
}

// 视频播放控制：绑定在容器上，避免 hover 覆盖层/按钮导致 mouseleave 抖动
const pauseTimers = new WeakMap<HTMLVideoElement, ReturnType<typeof setTimeout>>()

const warmUpOnce = (video: HTMLVideoElement) => {
    if ((video.dataset as any).weryWarm === '1') return
    ;(video.dataset as any).weryWarm = '1'
    try {
        // 不调用 load()，避免首帧闪烁/额外 206
        video.preload = 'auto'
    } catch {
        // ignore
    }
}

const handleCardMediaEnter = async (event: MouseEvent) => {
    const el = event.currentTarget as HTMLElement | null
    const video = el?.querySelector('video') as HTMLVideoElement | null
    if (!video) return

    const existing = pauseTimers.get(video)
    if (existing) {
        clearTimeout(existing)
        pauseTimers.delete(video)
    }

    warmUpOnce(video)
    if (!video.paused) return
    try {
        await video.play()
    } catch (error: any) {
        if (error?.name !== 'AbortError') {
            console.warn('视频播放失败:', error)
        }
    }
}

const handleCardMediaLeave = (event: MouseEvent) => {
    const el = event.currentTarget as HTMLElement | null
    const video = el?.querySelector('video') as HTMLVideoElement | null
    if (!video) return

    const existing = pauseTimers.get(video)
    if (existing) clearTimeout(existing)

    // hover 快速抖动时不要立刻 pause（会 Abort 网络/解码，导致下一次 hover 又要等）
    const timer = setTimeout(() => {
        pauseTimers.delete(video)
        if (!video.paused) video.pause()
    }, 250)
    pauseTimers.set(video, timer)
}

// 选择模板
const handleSelectTemplate = (item: TemplateItem) => {
    emit('select', item)
    handleClose()
}

// 关闭弹框
const handleClose = () => {
    emit('update:visible', false)
}

// 滚动处理 - 检测是否到底部
let scrollThrottle: ReturnType<typeof setTimeout> | null = null
const handleScroll = () => {
    // 视频和图片模板都支持无限滚动
    if (props.loadingMore || !props.hasMore) return
    
    // 节流处理
    if (scrollThrottle) return
    scrollThrottle = setTimeout(() => {
        scrollThrottle = null
    }, 200)
    
    const container = scrollContainerRef.value
    if (!container) return
    
    const { scrollTop, scrollHeight, clientHeight } = container
    // 距离底部 200px 时触发加载
    if (scrollHeight - scrollTop - clientHeight < 200) {
        emit('loadMore')
    }
}
</script>

<style lang="less" scoped>
.modal-container {
    width: 80vw;
    max-width: 1200px;
    height: 720px;
    max-height: 80vh;
    background: #22242D;
    border-radius: 24px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;
    border: 1px solid transparent;
    
    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 24px;
        padding: 1px;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        mask-composite: exclude;
        z-index: 1;
        pointer-events: none;
    }
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
}

.modal-title {
    color: #fff;
    font-size: 18px;
    font-weight: 500;
}

.close-btn {
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.2s;
    
    &:hover {
        opacity: 1;
    }
}

.tabs-container {
    display: flex;
    gap: 24px;
    padding: 16px 24px 0 24px;
    overflow-x: auto;
    flex-shrink: 0;
    
    &::-webkit-scrollbar {
        display: none;
    }
}

.tab-item {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.2s;
    padding-bottom: 10px;
    border-bottom: 2px solid transparent;
    
    &:hover {
        color: #fff;
    }
    
    &.active {
        color: #fff;
        font-weight: 500;
        border-bottom-color: #99F768;
    }
}

.templates-grid {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 24px;
}

.templates-grid-inner {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    
    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
    }
    
    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }
}

.template-card {
    cursor: pointer;
    transition: transform 0.2s;
    border-radius: 12px;
    overflow: hidden;
    background: #22242D;
}

.template-card:hover {
    transform: scale(1.02);
}

.template-card:hover .media-img {
    transform: scale(1.05);
}

.template-card:hover .use-template-btn {
    opacity: 1;
}

.card-media {
    position: relative;
    width: 100%;
    padding-bottom: 100%;
    overflow: hidden;
    background: #2a2a2a;
    border-radius: 12px 12px 0 0;
}

.media-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s;
}

.use-template-btn {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    padding: 8px 24px;
    background: #99F768;
    color: #000;
    font-size: 14px;
    font-weight: 400;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.3s;
    white-space: nowrap;
    z-index: 2;
}

.likes-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 58px;
    height: 32px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    color: #fff;
    font-size: 12px;
    z-index: 2;
}

.card-title {
    height: 44px;
    display: flex;
    align-items: center;
    padding: 0 12px;
    background: #22242D;
    color: #fff;
    font-size: 14px;
    font-weight: 400;
    min-width: 0;
}

.card-title__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    width: 100%;
}

.load-more-container {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 0;
    min-height: 60px;
}

.no-more-text {
    color: rgba(255, 255, 255, 0.4);
    font-size: 14px;
}
</style>

